import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FaFileAlt, FaPlus, FaBox } from "react-icons/fa";
import "../style/DeliveredChallan.css";
import api from "../api/axiosInstance";
import { TokenManager } from "../utils/tokenManager";
import InsertProduct from "./InsertProduct";
import { searchCustomers } from "../api/customerApi";
import { getAllGoods } from "../api/goodsApi";

const productsList = ["Product A", "Product B", "Product C", "Product D"];

function DeliveredChallan() {
  const navigate = useNavigate();
  const location = useLocation();

  const [showProductForm, setShowProductForm] = useState(false);
  const [showExtendedProductForm, setShowExtendedProductForm] = useState(false);
  const extendedFormRef = React.useRef(null);
  const [productForm, setProductForm] = useState({
    product: "",
    delivered: "",
    received: "",
    goodsItemId: null,
  });

  const [form, setForm] = useState({
    challanNumber: "",
    customer: "",
    customerId: "",
    challanType: "Delivered Challan",
    date: new Date().toISOString().split("T")[0],
    siteLocation: "",
    vehicleNo: "",
    transporter: "",
    driverDetails: "",
    deliveryDetails: "",
    poNo: "",
    depositType: "Deposit",
    depositAmount: "",
    depositNarration: "",
    products: [],
  });

  // State for tracking field errors
  const [fieldErrors, setFieldErrors] = useState({
    customer: false,
    siteLocation: false,
    vehicleNo: false,
    transporter: false,
    products: false,
  });

  // Customer search state
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Goods state for quick add product
  const [goods, setGoods] = useState([]);
  const [goodsLoading, setGoodsLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [customerInventory, setCustomerInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // Additional state for new product management
  const [products, setProducts] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [pageType, setPageType] = useState("delivered");

  // Fetch goods from API
  useEffect(() => {
    const fetchGoods = async () => {
      try {
        setGoodsLoading(true);
        const response = await getAllGoods();
        console.log("DeliveredChallan - Goods API response:", response);
        setGoods(response.data?.data || response.data || []);
      } catch (error) {
        console.error("DeliveredChallan - Error fetching goods:", error);
        toast.error("Failed to load products");
      } finally {
        setGoodsLoading(false);
      }
    };
    fetchGoods();
  }, []);

  /* RECEIVE PRODUCTS FROM INSERT PRODUCT PAGE */
  useEffect(() => {
    if (location.state?.products) {
      const mapped = location.state.products.map((p) => ({
        name: p.product,
        qty:
          form.challanType?.includes("Delivered") || !form.challanType
            ? Number(p.delivered)
            : Number(p.received),
      }));

      setForm((prev) => ({
        ...prev,
        products: mapped,
      }));
    }
  }, [location.state, form.challanType]);

  /* Scroll to extended form when it opens */
  useEffect(() => {
    if (showExtendedProductForm && extendedFormRef.current) {
      setTimeout(() => {
        extendedFormRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [showExtendedProductForm]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleProductFormChange(e) {
    if (e.target.name === "product") {
      // Use customer inventory first, then fall back to general goods
      const selectedGood = customerInventory.find(g => g.name === e.target.value) || 
                          goods.find(g => g.name === e.target.value);
      console.log("DeliveredChallan - Selected good:", selectedGood);
      setProductForm({ 
        ...productForm, 
        [e.target.name]: e.target.value,
        goodsItemId: selectedGood ? selectedGood.id : null
      });
    } else {
      setProductForm({ ...productForm, [e.target.name]: e.target.value });
    }
  }

  function handleAddProduct() {
    if (!productForm.product || (!productForm.delivered && !productForm.received)) {
      toast.error("Please fill product details");
      return;
    }

    const newProduct = {
      id: Date.now(),
      name: productForm.product,
      type: "",
      deliveredQty: form.challanType?.includes("Delivered") 
        ? Number(productForm.delivered) 
        : 0,
      receivedQty: 0,
      goodsItemId: productForm.goodsItemId,
      srNo: [],
      batchNo: [],
      openingBalance: 0,
      closingBalance: 0,
    };

    setProducts([...products, newProduct]);

    // Clear products field error when a product is added
    setFieldErrors(prev => ({ ...prev, products: false }));

    // Reset product form
    setProductForm({
      product: "",
      delivered: "",
      received: "",
      goodsItemId: null,
    });

    setShowProductForm(false);
    toast.success("Product added successfully!");
  }

  function handleRemoveProduct(index) {
    setForm(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
    toast.success("Product removed!");
  }

  function handleProductsAdded(products) {
    const mappedProducts = products.map(p => ({
      name: p.product,
      qty: form.challanType?.includes("Delivered") 
        ? Number(p.delivered) 
        : Number(p.received),
      goodsItemId: p.goodsItemId,
      type: p.type || "",
      srNo: p.srNo || [],
    }));

    setForm(prev => ({
      ...prev,
      products: [...prev.products, ...mappedProducts]
    }));

    setShowExtendedProductForm(false);
    toast.success(`${products.length} products added successfully!`);
  }

  // Customer search functions
  const handleCustomerSearch = async (searchTerm) => {
    setCustomerSearchTerm(searchTerm);
    
    if (searchTerm.length < 2) {
      setCustomers([]);
      setShowCustomerDropdown(false);
      return;
    }

    try {
      const response = await searchCustomers({ name: searchTerm });
      console.log("Customer search response:", response);
      setCustomers(response.data?.data || response.data || []);
      setShowCustomerDropdown(true);
    } catch (error) {
      console.error("Error searching customers:", error);
      setCustomers([]);
      setShowCustomerDropdown(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    setForm(prev => ({
      ...prev,
      customer: customer.name || customer.customerName,
      customerId: customer.id
    }));
    setCustomerSearchTerm(customer.name || customer.customerName);
    setShowCustomerDropdown(false);
    setCustomers([]);
    
    // Fetch customer inventory when customer is selected
    fetchCustomerInventory(customer.id);
  };

  // Fetch customer inventory
  const fetchCustomerInventory = async (customerId) => {
    if (!customerId) return;
    
    try {
      setInventoryLoading(true);
      const token = TokenManager.getToken();
      const response = await fetch(
        `https://ctmapi.zerlak.com/api/v1/challans/getCustomersInventoryItems/${customerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log("Customer inventory:", data);
        setCustomerInventory(Array.isArray(data) ? data : []);
      } else {
        console.error("Failed to fetch customer inventory");
        setCustomerInventory([]);
      }
    } catch (error) {
      console.error("Error fetching customer inventory:", error);
      setCustomerInventory([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    
    console.log("Form data:", form);
    console.log("Customer search term:", customerSearchTerm);
    console.log("Products:", products);

    // Reset field errors
    const newFieldErrors = {
      customer: !customerSearchTerm || !form.customerId,
      siteLocation: !form.siteLocation,
      vehicleNo: !form.vehicleNo,
      transporter: !form.transporter,
      products: products.length === 0,
    };
    
    setFieldErrors(newFieldErrors);

    // Check for validation errors and build specific error message
    const errorMessages = [];
    if (newFieldErrors.customer) errorMessages.push("Customer");
    if (newFieldErrors.siteLocation) errorMessages.push("Site Location");
    if (newFieldErrors.vehicleNo) errorMessages.push("Vehicle Number");
    if (newFieldErrors.transporter) errorMessages.push("Transporter");
    if (newFieldErrors.products) errorMessages.push("Products");

    if (errorMessages.length > 0) {
      console.log("Validation failed - missing fields:", errorMessages);
      toast.error(`Please fill required fields: ${errorMessages.join(', ')}`);
      return;
    }

    const token = TokenManager.getToken();
    if (!token) {
      console.log("Validation failed - no token");
      toast.error("Session expired. Please login again.");
      navigate("/");
      return;
    }

    const payload = {
      customerId: form.customerId,
      customerName: form.customer,
      siteLocation: form.siteLocation,
      vehicleNumber: form.vehicleNo,
      deliveredChallanNo: "",
      driverName: form.driverDetails,
      driverNumber: "",
      transporter: form.transporter,
      lrNumber: "",
      challanNumber: form.challanNumber === "AUTO" ? "" : form.challanNumber,
      challanType: "DELIVERED",
      date: form.date,
      purchaseOrderNo: form.poNo,
      returnedAmount:
        form.depositType === "Returned" ? Number(form.depositAmount) : 0,
      depositeNarration: form.depositNarration,
      deliveryDetails: form.deliveryDetails,

      items: products
        .filter((p) => p.deliveredQty && p.deliveredQty > 0)
        .map((p, idx) => ({
          goodsItemId: p.goodsItemId || idx + 1,
          name: p.name,
          type: p.type || "",
          deliveredQty: Number(p.deliveredQty),
          receivedQty: 0,
          srNo: p.srNo || [],
        })),
    };

    try {
      console.log("Submitting payload:", payload);
      const response = await api.post("challans/create", payload);
      console.log("API Response:", response);
      
      if (response.status === 201 || response.status === 200) {
        toast.success("Delivered Challan created successfully!");
        handleReset();
      } else {
        toast.error("Unexpected response status: " + response.status);
      }
    } catch (err) {
      console.error("API Error:", err);
      console.error("Error Response:", err.response?.data);
      toast.error("Error: " + (err.response?.data?.message || err.message));
    }
  }

  function handleReset() {
    setForm({
      challanNumber: "",
      customer: "",
      customerId: "",
      challanType: "Delivered Challan",
      date: new Date().toISOString().split("T")[0],
      siteLocation: "",
      vehicleNo: "",
      transporter: "",
      driverDetails: "",
      deliveryDetails: "",
      poNo: "",
      depositType: "Deposit",
      depositAmount: "",
      depositNarration: "",
      products: [],
    });
    setCustomerSearchTerm("");
    setShowCustomerDropdown(false);
    setCustomers([]);
    setProducts([]);
    setSavedProducts([]);
    // Clear field errors
    setFieldErrors({
      customer: false,
      siteLocation: false,
      vehicleNo: false,
      transporter: false,
      products: false,
    });
  }

  // Additional functions for new product management
  const addProductRow = () => {
    const newProduct = {
      id: Date.now(),
      name: "",
      type: "",
      deliveredQty: "",
      receivedQty: "",
      srNo: ""
    };
    setProducts([...products, newProduct]);
  };

  const removeProductRow = (index) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...products];
    updatedProducts[index][field] = value;
    setProducts(updatedProducts);
  };

  const handleDeleteProduct = (index) => {
    setSavedProducts(savedProducts.filter((_, i) => i !== index));
  };

  const handleSaveChallan = (e) => {
    // Create a synthetic form event and call handleSubmit to actually create the challan
    const syntheticEvent = {
      preventDefault: () => {}
    };
    handleSubmit(syntheticEvent);
  };

  // Mock ProductTable component (you may need to import or create this)
  const ProductTable = ({ prefix, pageType, products, allProducts, onUpdate, onRemove, customerInventory, inventoryLoading }) => {
    // Add state for product search dropdowns
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdowns, setShowProductDropdowns] = useState({});
  const [showBatchDropdowns, setShowBatchDropdowns] = useState({});
  const [showSrnoDropdowns, setShowSrnoDropdowns] = useState({});
    const [filteredGoods, setFilteredGoods] = useState({});

    const setShowBatchDropdown = (index, show) => {
      setShowBatchDropdowns(prev => ({ ...prev, [index]: show }));
    };

    const setShowSrnoDropdown = (index, show) => {
      setShowSrnoDropdowns(prev => ({ ...prev, [index]: show }));
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        const batchContainers = document.querySelectorAll('.batch-srno-container');
        batchContainers.forEach((container, index) => {
          if (!container.contains(event.target)) {
            setShowBatchDropdowns(prev => ({ ...prev, [index]: false }));
          }
        });
        
        const srnoContainers = document.querySelectorAll('.srno-container');
        srnoContainers.forEach((container, index) => {
          if (!container.contains(event.target)) {
            setShowSrnoDropdowns(prev => ({ ...prev, [index]: false }));
          }
        });
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleProductSearch = (index, searchTerm) => {
      setProductSearchTerms(prev => ({ ...prev, [index]: searchTerm }));
      
      if (searchTerm.length < 2) {
        setFilteredGoods(prev => ({ ...prev, [index]: [] }));
        setShowProductDropdowns(prev => ({ ...prev, [index]: false }));
        return;
      }

      // Use customer inventory first, then fall back to general goods
      const searchSource = customerInventory.length > 0 ? customerInventory : goods;
      const filtered = searchSource.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredGoods(prev => ({ ...prev, [index]: filtered }));
      setShowProductDropdowns(prev => ({ ...prev, [index]: true }));
    };

    const handleProductSelect = async (index, selectedGood) => {
      onUpdate(index, 'name', selectedGood.name);
      // Auto-fill type if available from goods data
      if (selectedGood && selectedGood.type) {
        onUpdate(index, 'type', selectedGood.type);
      }
      setProductSearchTerms(prev => ({ ...prev, [index]: selectedGood.name }));
      setShowProductDropdowns(prev => ({ ...prev, [index]: false }));
      
      // Fetch cylinder data when product is selected
      try {
        const cylinderName = selectedGood.name; 
        const response = await api.get(`cylinderMaster/getSrno?cylinderName=${encodeURIComponent(cylinderName)}`);
        console.log("Cylinder data fetched:", response.data);
        
        // Store cylinder data for this product row
        if (response.data && Array.isArray(response.data)) {
          onUpdate(index, 'cylinderData', response.data);
          // You can also store the first batch and srno if needed
          if (response.data.length > 0) {
            onUpdate(index, 'selectedBatch', response.data[0].batchNo);
            onUpdate(index, 'selectedSrno', response.data[0].srno);
          }
        }
      } catch (error) {
        console.error("Error fetching cylinder data:", error);
        toast.error("Failed to fetch cylinder data");
      }
    };

    return (
      <div className="product-table">
        {products.map((product, index) => (
          <div key={product.id || index} className="product-row">
            <div className="product-search-container">
              <label>Product Name</label>
              <div className="product-search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search product..."
                  value={productSearchTerms[index] || product.name || ''}
                  onChange={(e) => handleProductSearch(index, e.target.value)}
                  onFocus={() => {
                    const searchTerm = productSearchTerms[index] || product.name || '';
                    if (searchTerm.length >= 2) {
                      setShowProductDropdowns(prev => ({ ...prev, [index]: true }));
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowProductDropdowns(prev => ({ ...prev, [index]: false })), 200)}
                  className="product-search-input"
                />
              </div>
              {showProductDropdowns[index] && (
                <div className="product-dropdown">
                  {filteredGoods[index]?.length === 0 && (productSearchTerms[index] || '').length >= 2 ? (
                    <div className="product-no-results">
                      <div className="no-results-icon">°</div>
                      <div className="no-results-text">No products found</div>
                      <div className="no-results-hint">Try searching with different keywords</div>
                    </div>
                  ) : (
                    (filteredGoods[index] || (customerInventory.length > 0 ? customerInventory : goods)).map((item) => (
                      <div
                        key={item.id}
                        className="product-option"
                        onClick={() => handleProductSelect(index, item)}
                      >
                        <div className="product-option-main">
                          <div className="product-name">
                            {item.name}
                          </div>
                          {item.type && (
                            <div className="product-type">
                              Type: {item.type}
                            </div>
                          )}
                        </div>
                        <div className="product-select-icon">▼</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div>
              <label>Type</label>
              <input
                type="text"
                placeholder="Type"
                value={product.type}
                onChange={(e) => onUpdate(index, 'type', e.target.value)}
                style={{
                  background: "white",
                  cursor: "text"
                }}
              />
            </div>
            <div>
              <label>Delivered Qty</label>
              <input
                type="number"
                placeholder="Delivered Qty"
                value={product.deliveredQty}
                onChange={(e) => onUpdate(index, 'deliveredQty', e.target.value)}
              />
            </div>
            <div className="batch-srno-container">
              <label>Batch No</label>
              <input
                type="text"
                placeholder="Batch No"
                value={product.selectedBatch || ''}
                onChange={(e) => onUpdate(index, 'selectedBatch', e.target.value)}
                onFocus={() => setShowBatchDropdown(index, true)}
                readOnly={product.cylinderData && product.cylinderData.length > 0}
                style={{
                  background: product.cylinderData && product.cylinderData.length > 0 ? "#f3f4f6" : "white",
                  cursor: product.cylinderData && product.cylinderData.length > 0 ? "pointer" : "text"
                }}
              />
              {showBatchDropdowns[index] && product.cylinderData && product.cylinderData.length > 0 && (
                <div className="batch-dropdown">
                  {product.cylinderData.map((cylinder, idx) => (
                    <div
                      key={idx}
                      className="batch-option"
                      onClick={() => {
                        onUpdate(index, 'selectedBatch', cylinder.batchNo);
                        onUpdate(index, 'selectedSrno', cylinder.srno);
                        setShowBatchDropdowns(prev => ({ ...prev, [index]: false }));
                      }}
                    >
                      <div className="batch-info">
                        <div className="batch-number">{cylinder.batchNo}</div>
                        <div className="sr-number">{cylinder.srno}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="srno-container">
              <label>Sr No</label>
              <input
                type="text"
                placeholder="Sr No"
                value={product.selectedSrno || product.srNo || ''}
                onChange={(e) => onUpdate(index, 'selectedSrno', e.target.value)}
                onFocus={() => setShowSrnoDropdown(index, true)}
                readOnly={product.cylinderData && product.cylinderData.length > 0}
                style={{
                  background: product.cylinderData && product.cylinderData.length > 0 ? "#f3f4f6" : "white",
                  cursor: product.cylinderData && product.cylinderData.length > 0 ? "pointer" : "text"
                }}
              />
              {showSrnoDropdowns[index] && product.cylinderData && product.cylinderData.length > 0 && (
                <div className="srno-dropdown">
                  {product.cylinderData.map((cylinder, idx) => (
                    <div
                      key={idx}
                      className="srno-option"
                      onClick={() => {
                        onUpdate(index, 'selectedSrno', cylinder.srno);
                        onUpdate(index, 'selectedBatch', cylinder.batchNo);
                        setShowSrnoDropdowns(prev => ({ ...prev, [index]: false }));
                      }}
                    >
                      <div className="srno-info">
                        <div className="srno-number">{cylinder.srno}</div>
                        <div className="batch-display">Batch: {cylinder.batchNo}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => products.length > 1 && onRemove(index)}
              disabled={products.length <= 1}
              style={{
                background: products.length <= 1 ? "#d1d5db" : "#ff4d4f",
                border: "none",
                color: products.length <= 1 ? "#9ca3af" : "#fff",
                padding: "5px 10px",
                borderRadius: "6px",
                cursor: products.length <= 1 ? "not-allowed" : "pointer"
              }}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="dc-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="dc-card">
        <div className="dc-header">
          <div className="dc-header-left">
            <FaFileAlt className="dc-icon" />
            <h2>Delivered Challan Entry</h2>
          </div>
        </div>

      <form onSubmit={handleSubmit}>
          <div className="dc-grid">
            {/* ================= CUSTOMER & LOCATION ================= */}
            <div className="dc-section">
              <div className="dc-section-title">Customer & Location</div>
              <div className="dc-grid" style={{ padding: 0, gap: '20px' }}>
                <div className="dc-field full">
                  <label>Customer Name *</label>
                  <div className="customer-search-container">
                    <div className="customer-search-input-wrapper">
                      <input
                        name="customer"
                        value={customerSearchTerm}
                        onChange={(e) => {
                          handleCustomerSearch(e.target.value);
                          // Clear error when user starts typing
                          if (fieldErrors.customer) {
                            setFieldErrors(prev => ({ ...prev, customer: false }));
                          }
                        }}
                        onFocus={() => customerSearchTerm.length >= 2 && setShowCustomerDropdown(true)}
                        onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                        placeholder="Search customer name..."
                        required
                        className="customer-search-input"
                        style={{
                          border: fieldErrors.customer ? '2px solid #ef4444' : '1px solid #e2e8f0',
                          backgroundColor: fieldErrors.customer ? '#fef2f2' : 'white'
                        }}
                      />
                      {fieldErrors.customer && (
                        <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                          Please select a customer from the dropdown
                        </div>
                      )}
                      <div className="customer-search-icon">
                        {showCustomerDropdown ? '▲' : '▼'}
                      </div>
                    </div>
                    {showCustomerDropdown && (
                      <div className="customer-dropdown">
                        {customers.length === 0 && customerSearchTerm.length >= 2 ? (
                          <div className="customer-no-results">
                            <div className="no-results-icon">🔍</div>
                            <div className="no-results-text">No customers found</div>
                            <div className="no-results-hint">Try searching with different keywords</div>
                          </div>
                        ) : (
                          customers.map((customer) => (
                            <div
                              key={customer.id}
                              className="customer-option"
                              onClick={() => handleCustomerSelect(customer)}
                            >
                              <div className="customer-option-main">
                                <div className="customer-name">
                                  {customer.name || customer.customerName}
                                </div>
                                {customer.email && (
                                  <div className="customer-email">
                                    {customer.email}
                                  </div>
                                )}
                                {customer.phone && (
                                  <div className="customer-phone">
                                    📞 {customer.phone}
                                  </div>
                                )}
                              </div>
                              <div className="customer-select-icon">✓</div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="dc-field full">
                  <label>Site Location *</label>
                  <input
                    name="siteLocation"
                    value={form.siteLocation}
                    onChange={(e) => {
                      handleChange(e);
                      // Clear error when user starts typing
                      if (fieldErrors.siteLocation) {
                        setFieldErrors(prev => ({ ...prev, siteLocation: false }));
                      }
                    }}
                    placeholder="Enter site location"
                    required
                    style={{
                      border: fieldErrors.siteLocation ? '2px solid #ef4444' : '1px solid #e2e8f0',
                      backgroundColor: fieldErrors.siteLocation ? '#fef2f2' : 'white'
                    }}
                  />
                  {fieldErrors.siteLocation && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      Site location is required
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ================= BASIC INFO ================= */}
            <div className="dc-section">
              <div className="dc-section-title">Basic Information</div>
              <div className="dc-grid" style={{ padding: 0, gap: '20px' }}>
                <div className="dc-field full">
                  <label>Date *</label>
                  <input
                    name="date"
                    type="date"
                    value={form.date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="dc-field full">
                  <label>Challan Type</label>
                  <input
                    type="text"
                    value={form.challanType}
                    disabled
                    style={{
                      background: "#f3f4f6",
                      color: "#6b7280",
                      cursor: "not-allowed"
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ================= TRANSPORT DETAILS ================= */}
            <div className="dc-section">
              <div className="dc-section-title">Transport Details</div>
              <div className="dc-grid" style={{ padding: 0, gap: '20px' }}>
                <div className="dc-field full">
                  <label>Vehicle Number *</label>
                  <input
                    name="vehicleNo"
                    value={form.vehicleNo}
                    onChange={(e) => {
                      handleChange(e);
                      // Clear error when user starts typing
                      if (fieldErrors.vehicleNo) {
                        setFieldErrors(prev => ({ ...prev, vehicleNo: false }));
                      }
                    }}
                    placeholder="Enter vehicle number"
                    required
                    style={{
                      border: fieldErrors.vehicleNo ? '2px solid #ef4444' : '1px solid #e2e8f0',
                      backgroundColor: fieldErrors.vehicleNo ? '#fef2f2' : 'white'
                    }}
                  />
                  {fieldErrors.vehicleNo && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      Vehicle number is required
                    </div>
                  )}
                </div>

                <div className="dc-field full">
                  <label>Transporter *</label>
                  <input
                    name="transporter"
                    value={form.transporter}
                    onChange={(e) => {
                      handleChange(e);
                      // Clear error when user starts typing
                      if (fieldErrors.transporter) {
                        setFieldErrors(prev => ({ ...prev, transporter: false }));
                      }
                    }}
                    placeholder="Enter transporter details"
                    required
                    style={{
                      border: fieldErrors.transporter ? '2px solid #ef4444' : '1px solid #e2e8f0',
                      backgroundColor: fieldErrors.transporter ? '#fef2f2' : 'white'
                    }}
                  />
                  {fieldErrors.transporter && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      Transporter is required
                    </div>
                  )}
                </div>

                <div className="dc-field full">
                  <label>Driver Details</label>
                  <input
                    name="driverDetails"
                    value={form.driverDetails}
                    onChange={handleChange}
                    placeholder="Name Mobile (e.g., John 9876543210)"
                  />
                </div>
              </div>
            </div>

            {/* ================= DELIVERY INFO ================= */}
            <div className="dc-section">
              <div className="dc-section-title">Delivery Information</div>
              <div className="dc-grid" style={{ padding: 0, gap: '20px' }}>
                <div className="dc-field full">
                  <label>Delivery Details</label>
                  <input
                    name="deliveryDetails"
                    value={form.deliveryDetails}
                    onChange={handleChange}
                    placeholder="Enter delivery details"
                  />
                </div>

                <div className="dc-field full">
                  <label>PO No.</label>
                  <input
                    name="poNo"
                    value={form.poNo}
                    onChange={handleChange}
                    placeholder="Enter PO number"
                  />
                </div>

                <div className="dc-field">
                  <label>Challan Number</label>
                  <input
                    name="challanNumber"
                    value={form.challanNumber}
                    onChange={handleChange}
                    placeholder="Enter challan number (optional)"
                  />
                </div>
              </div>
            </div>

            {/* ================= DEPOSIT INFO ================= */}
            <div className="dc-section full-width">
              <div className="dc-section-title">Deposit Information</div>
              <div className="dc-grid" style={{ padding: 0, gap: '20px' }}>
                <div className="dc-field">
                  <label>Deposit Type</label>
                  <select
                    name="depositType"
                    value={form.depositType}
                    onChange={handleChange}
                  >
                    <option value="Deposit">Deposit</option>
                    <option value="Returned">Returned</option>
                  </select>
                </div>

                <div className="dc-field">
                  <label>Deposit Amount</label>
                  <input
                    name="depositAmount"
                    type="number"
                    value={form.depositAmount}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="dc-field full">
                  <label>Deposit Narration</label>
                  <input
                    name="depositNarration"
                    value={form.depositNarration}
                    onChange={handleChange}
                    placeholder="Enter narration"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="dc-section">
            <h3>Products</h3>
            <div className="dc-products-info">
              <p>
                {savedProducts.length > 0
                  ? `${savedProducts.length} saved product(s)` 
                  : "No saved products yet"}
                {products.length > 0 && ` · ${products.length} draft row(s)`}
              </p>
            </div>

            {products.length === 0 && (
              <div className="dc-product-empty-state" style={{
                border: fieldErrors.products ? '2px solid #ef4444' : '1px dashed #d1d5db',
                backgroundColor: fieldErrors.products ? '#fef2f2' : '#f9fafb',
                padding: '20px',
                textAlign: 'center',
                borderRadius: '8px'
              }}>
                <p style={{ 
                  color: fieldErrors.products ? '#ef4444' : '#6b7280',
                  margin: '0 0 15px 0'
                }}>
                  {fieldErrors.products ? 'At least one product is required' : 'No products added yet'}
                </p>
                <button
                  type="button"
                  className="dc-btn-primary-modern"
                  onClick={() => {
                    addProductRow();
                    // Clear error when user adds a product
                    if (fieldErrors.products) {
                      setFieldErrors(prev => ({ ...prev, products: false }));
                    }
                  }}
                >
                  <span className="dc-btn-icon">+</span>
                  Add Product
                </button>
              </div>
            )}

            {products.length > 0 && (
              <div>
                <ProductTable
                  prefix="dc"
                  pageType={pageType}
                  products={products}
                  allProducts={allProducts}
                  customerInventory={customerInventory}
                  inventoryLoading={inventoryLoading}
                  onUpdate={handleProductChange}
                  onRemove={removeProductRow}
                />
                <div className="dc-product-add-row">
                  <button
                    type="button"
                    className="dc-btn-primary-modern"
                    onClick={addProductRow}
                  >
                    <span className="dc-btn-icon">+</span>
                    Add Product
                  </button>
                </div>
              </div>
            )}

            {savedProducts.length > 0 && (
              <div className="dc-saved-product-list">
                <div className="dc-product-grid-header saved-header">
                  <div>Product</div>
                  <div>Type</div>
                  <div>Delivered Qty</div>
                  <div>Sr No</div>
                  <div>Action</div>
                </div>
                <div>
                  {savedProducts.map((item, index) => (
                    <div key={index} className="dc-saved-product-row">
                      <div>{item.name}</div>
                      <div>{item.type}</div>
                      <div>{item.deliveredQty}</div>
                      <div>{item.srNo}</div>
                      <div>
                        <button
                          onClick={() => handleDeleteProduct(index)}
                          style={{
                            background: "#ff4d4f",
                            border: "none",
                            color: "#fff",
                            padding: "5px 10px",
                            borderRadius: "6px",
                            cursor: "pointer"
                          }}
                        >
                          
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="dc-actions">
            <button
              type="button"
              className="dc-btn-outline"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className="dc-btn-primary"
              onClick={handleSaveChallan}
            >
              Save Challan
            </button>
          </div>
        </form>
      </div>

      {/* Extended Product Form - Inline */}
      {showExtendedProductForm && (
        <div className="dc-extended-product-section" ref={extendedFormRef}>
          <div className="dc-extended-product-header">
            <h3><FaBox /> Add Multiple Products</h3>
            <button 
              className="dc-extended-close-btn"
              onClick={() => setShowExtendedProductForm(false)}
            >
              ×
            </button>
          </div>
          <div className="dc-extended-product-content">
            <InsertProduct
              onProductsAdded={handleProductsAdded}
              onClose={() => setShowExtendedProductForm(false)}
              isInModal={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveredChallan;
